// Classifies a CAPI Machine's scale-up health from fields already present on
// the Machine CR. CAPO reports VM-create errors (quota exceeded, no valid
// host) as retryable False conditions - never a terminal failure - and a
// kubeadm join failure produces no CR signal at all, so both need derivation.

export enum MachineHealthState {
  Ok = 'ok',
  Progressing = 'progressing',
  Degraded = 'degraded', // error reported, CAPI/CAPO still retrying
  Stuck = 'stuck', // no error reported but not progressing (join timeout)
  Slow = 'slow', // provisioning taking longer than expected, not yet an error
  Failed = 'failed', // terminal - CAPI gave up
}

export interface MachineHealth {
  state: MachineHealthState
  reason?: string
  message?: string
}

// kaapi deploys no MachineHealthCheck, so nothing in the stack ever detects
// or remediates a machine whose node never joins - this timeout is the only
// stuck-join detector. 15m matches CAPI's kubeadm bootstrap token TTL
// (upstream default, not overridden by kaapi) and buffers slow-but-healthy
// provisioning (CAPO instance-create timeout is raised to 10m, KAAP-1495).
export const NODE_JOIN_TIMEOUT_MS = 15 * 60 * 1000
// Non-Error conditions are normal early in provisioning; only surface them
// once the machine is old enough that they indicate a real problem.
export const CONDITION_GRACE_MS = 5 * 60 * 1000
export const SERVER_EVENT_STALE_MS = 15 * 60 * 1000

// BYOH has no VM to create - the host pre-exists - so neither of the two things
// that set NODE_JOIN_TIMEOUT_MS to 15m applies: there is no kubeadm bootstrap
// token in play before the join, and CAPO's 10m instance-create timeout
// (KAAP-1495) has no BYOH analogue. Measured on a live BYOH DU, healthy hosts
// reported K8sComponentsInstallationSucceeded=True 48s, 73s and 118s after
// their Machine was created, so 4m is ~2x and 8m ~4x the slowest healthy
// observation. KAAP-2268.
export const BYOH_PROVISION_SLOW_MS = 4 * 60 * 1000
export const BYOH_PROVISION_STUCK_MS = 8 * 60 * 1000

export const BYOHOSTS_UNAVAILABLE_REASON = 'BYOHostsUnavailable'
// The ByoMachine reports BYOHostReady=False within seconds of the Machine being
// created, and a healthy machine passes through that state during normal host
// selection, so this needs a grace period. On a live cluster the condition
// appeared 4s after creation. 2m clears normal selection comfortably and still
// fires 2x earlier than the BYOH_PROVISION_SLOW_MS soft stage. KAAP-2268.
export const BYOHOST_UNAVAILABLE_GRACE_MS = 2 * 60 * 1000

const ERROR_CONDITION_TYPES = ['InfrastructureReady', 'BootstrapReady']

export const CONTROL_PLANE_UNREACHABLE_REASON = 'ControlPlaneUnreachable'
export const INFRA_PROVISION_SLOW_REASON = 'InfrastructureProvisionSlow'

// Used when the infrastructure provider parks a server in ERROR without
// recording why. The instance fault is the only place the real cause lives and
// it is not mirrored onto any CR the UI can read, so the wording points the
// operator at the VM - the error modal offers "Open VM Console" for it.
// Kept short: banners append their own suffix.
export const INSTANCE_ERROR_MESSAGE =
  'The virtual machine for this node has failed and will not recover on its own. ' +
  'No cause was reported on the cluster resources - use Open VM Console to see ' +
  'the fault reported against the VM.'

export const BYOHOSTS_UNAVAILABLE_MESSAGE =
  'No bare metal host is available to provision this node. Every host matching this ' +
  "cluster's host selector is already claimed, or is still being released from a deleted " +
  'cluster. Onboard another host, or check the Physical Hosts page for a host stuck in an ' +
  'unregistering state.'

export const CONTROL_PLANE_UNREACHABLE_MESSAGE =
  "The management cluster has lost API connectivity to the workload cluster's control plane. " +
  'Node readiness cannot be verified until the connection is restored. ' +
  'Check the control plane nodes via console/SSH.'

// The management cluster signals a lost connection to the workload control
// plane through two conditions, depending on CAPI version and tunnel
// implementation, so both have to be checked:
//   - CCPFRPSReady/WaitingForTunnel      - kaapi's reverse-proxy tunnel is down
//   - RemoteConnectionProbe/ProbeFailed  - CAPI 1.7+ status.v1beta2 probe
//
// ControlPlaneReady is deliberately NOT one of them. It rolls up KubeadmControlPlane
// readiness (replica count, etcd, CP components), so it stays False with
// reason=ScalingUp for the entire 1/3 -> 3/3 scale-up of a control plane the
// management cluster can reach perfectly well. Using it flagged every healthy
// multi-master cluster as "Connection Lost" for the whole bring-up window.
//
// Both remaining conditions are also gated on ControlPlaneInitialized: before the
// first CP node finishes kubeadm init, WaitingForTunnel and ProbeFailed are the
// normal bootstrap state - the connection was never established, so it cannot
// have been lost.
//
// Shared by getMachineHealth and the node group status cell so both views
// agree on what "Connection Lost" means.
export function hasControlPlaneConnectionIssue(cluster): boolean {
  const conditions = [
    ...(cluster?.status?.conditions || []),
    ...(cluster?.status?.v1beta2?.conditions || []),
  ]
  const controlPlaneWasInitialized = conditions.some(
    (c) => c?.type === 'ControlPlaneInitialized' && c?.status === 'True',
  )
  if (!controlPlaneWasInitialized) return false
  return conditions.some(
    (c) =>
      (c?.type === 'CCPFRPSReady' && c?.status === 'False' && c?.reason === 'WaitingForTunnel') ||
      (c?.type === 'RemoteConnectionProbe' && c?.status === 'False' && c?.reason === 'ProbeFailed'),
  )
}

// BYOH clusters back each node with a pre-registered physical host, not a VM,
// so the VM-flavoured timeout wording below is factually wrong for them: there
// is no hypervisor to check and no VM console to open. Derived from the Machine
// itself rather than passed in, so no call site has to change.
export const isByoMachine = (machine): boolean =>
  machine?.spec?.infrastructureRef?.kind === 'ByoMachine'

export function getMachineHealth(
  machine,
  now: number = Date.now(),
  openStackServer?,
  openStackServerEvent?,
  cluster?,
  byoHost?,
  byoMachine?,
): MachineHealth {
  const status = machine?.status
  const phase = status?.phase || ''

  if (status?.failureMessage || status?.failureReason || phase === 'Failed') {
    return {
      state: MachineHealthState.Failed,
      reason: status?.failureReason || 'MachineFailed',
      message: status?.failureMessage || 'Machine entered a terminal Failed state',
    }
  }

  if (phase === 'Deleting') return { state: MachineHealthState.Progressing }

  const createdAt = Date.parse(machine?.metadata?.creationTimestamp)
  const age = Number.isFinite(createdAt) ? now - createdAt : 0
  const conditions = status?.conditions || []

  const errorCondition =
    conditions.find((c) => c?.status === 'False' && c?.severity === 'Error' && c?.message) ||
    (age > CONDITION_GRACE_MS
      ? conditions.find(
          (c) => c?.status === 'False' && c?.message && ERROR_CONDITION_TYPES.includes(c?.type),
        )
      : undefined)
  if (errorCondition) {
    return {
      state: MachineHealthState.Degraded,
      reason: errorCondition.reason,
      message: errorCondition.message,
    }
  }

  // CAPO reports the real VM-create failure only on the OpenStackServer CR
  // (two hops below the Machine), as a False/Error condition. It is terminal
  // and parked ("Not reconciling server in error state"), so surface it
  // immediately with no grace - preempting the generic join-timeout below.
  //
  // The condition is frequently message-less: when the hypervisor puts the
  // instance into ERROR, CAPO writes reason=InstanceStateError and leaves the
  // fault on the server without copying it. instanceState is then the only
  // signal, so match on either and synthesize the wording. Requiring a message
  // here made a hard VM failure render as "still provisioning" until the 15
  // minute timeout.
  //
  // instanceState=ERROR is reported as Failed, not Degraded: CAPO logs "Not
  // reconciling server in error state" and stops, so nothing will retry and the
  // operator has to delete the machine to get a replacement - which is what the
  // Failed remediation text tells them. An Error condition without an ERROR
  // instance is a pre-create failure (quota, no valid host) that CAPO does keep
  // retrying, so that stays Degraded.
  const openStackServerError = openStackServer?.status?.conditions?.find(
    (c) => c?.status === 'False' && c?.severity === 'Error',
  )
  const instanceStateError = openStackServer?.status?.instanceState === 'ERROR'
  if (openStackServerError || instanceStateError) {
    return {
      state: instanceStateError ? MachineHealthState.Failed : MachineHealthState.Degraded,
      reason: openStackServerError?.reason || 'InstanceStateError',
      message: openStackServerError?.message || INSTANCE_ERROR_MESSAGE,
    }
  }

  // Neutron port-allocation and similar pre-VM failures are reported only as
  // Warning Events on the OpenStackServer CR (CAPO never writes a condition for
  // them). Surface the most recent one while it is still fresh so the operator
  // can see the real cause instead of waiting for the join timeout.
  if (openStackServerEvent) {
    const eventAge = now - Date.parse(openStackServerEvent?.lastTimestamp || '0')
    if (Number.isFinite(eventAge) && eventAge < SERVER_EVENT_STALE_MS) {
      return {
        state: MachineHealthState.Degraded,
        reason: openStackServerEvent.reason || 'InfrastructureError',
        message: openStackServerEvent.message,
      }
    }
  }

  // BYOH reports a failed Kubernetes install or cluster join only on the ByoHost
  // CR, as K8sNodeBootstrapSucceeded=False with severity Error - written by the
  // agent on the host the moment kubeadm fails, so it needs no grace period.
  //
  // This is the only way to tell a failed join from "no host yet" on BYOH. The
  // age-based branch below cannot: it keys off phase Provisioned / providerID,
  // but for BYOH the provider sets providerID by patching the Node object in the
  // workload cluster (updateNodeProviderID), which only exists once kubeadm join
  // already succeeded. providerID set therefore *means* the join worked, so a
  // join failure never reaches NodeJoinTimeout and would otherwise be reported
  // as InfrastructureProvisionTimeout - blaming host selection or the host OS
  // for what is actually a kubeadm/connectivity failure.
  //
  // Degraded, not Stuck: the agent resets the node and retries on its next
  // reconcile, so this can clear on its own.
  //
  // Gated on the machine kind as well as on byoHost being present. The selector
  // only ever resolves a ByoHost for a ByoMachine, but gating here too keeps a
  // CAPO machine's verdict provably independent of this argument rather than
  // dependent on a caller getting it right.
  const byoBootstrapError = isByoMachine(machine)
    ? byoHost?.status?.conditions?.find(
        (c) =>
          c?.type === 'K8sNodeBootstrapSucceeded' &&
          c?.status === 'False' &&
          c?.severity === 'Error',
      )
    : undefined
  if (byoBootstrapError) {
    return {
      state: MachineHealthState.Degraded,
      reason: byoBootstrapError.reason || 'NodeBootstrapFailed',
      message:
        byoBootstrapError.message ||
        'The Kubernetes install or the cluster join failed on the host and was rolled back. ' +
          'It will be retried automatically. Check /var/log/pf9/byoh/byoh-agent.log on the host ' +
          'for the error.',
    }
  }

  // BYOH reports "no host to claim" only on the ByoMachine CR, as
  // BYOHostReady=False with reason=BYOHostsUnavailable - written the moment host
  // selection fails to find a free host matching the cluster's selector. Without
  // this branch the operator sees the generic InfrastructureProvisionTimeout
  // wording below ("check the host OS", "check byoh-agent.log on the host"),
  // which is actively wrong here: no host was ever claimed, so there is no host
  // to check the OS of and no byoh-agent.log to read.
  //
  // Degraded, not Stuck: the provider keeps re-selecting on every reconcile, so
  // this clears itself the moment a matching host frees up - the same
  // "error reported, CAPI/CAPO still retrying" definition Degraded already uses
  // elsewhere in this function, not a dead end that needs the timing branches.
  //
  // Gated on the machine kind as well as on byoMachine being present, mirroring
  // the byoBootstrapError branch above: the selector only ever resolves a
  // ByoMachine for a ByoMachine-kind Machine, but gating here too keeps a CAPO
  // machine's verdict provably independent of this argument rather than
  // dependent on a caller getting it right.
  //
  // Severity is deliberately not checked. The provider writes this condition at
  // severity Info (confirmed on a live cluster), not Error - unlike the
  // byoBootstrapError condition above, which is always Error. Requiring Error
  // here would silently miss every real occurrence of this failure.
  const byoHostUnavailable = isByoMachine(machine)
    ? byoMachine?.status?.conditions?.find(
        (c) =>
          c?.type === 'BYOHostReady' &&
          c?.status === 'False' &&
          c?.reason === BYOHOSTS_UNAVAILABLE_REASON,
      )
    : undefined
  if (byoHostUnavailable && age > BYOHOST_UNAVAILABLE_GRACE_MS) {
    return {
      state: MachineHealthState.Degraded,
      reason: BYOHOSTS_UNAVAILABLE_REASON,
      message: BYOHOSTS_UNAVAILABLE_MESSAGE,
    }
  }

  // Control Plane Connectivity Check
  // Evaluates whether the management cluster can communicate with the workload API.
  // We check this BEFORE the 15-minute generic timeout below, because if the
  // control plane is unreachable, CAPI can't update nodeRef anyway, and we want
  // to surface the connection drop immediately instead of blaming the node.
  if (cluster && hasControlPlaneConnectionIssue(cluster)) {
    return {
      state: MachineHealthState.Degraded,
      reason: CONTROL_PLANE_UNREACHABLE_REASON,
      message: CONTROL_PLANE_UNREACHABLE_MESSAGE,
    }
  }

  // If the machine has no k8s nodeRef:
  // Case A: VM exists (Provisioned or providerID set) -> NodeJoinTimeout (kubeadm / bootstrap
  //   issue) after NODE_JOIN_TIMEOUT_MS (15 min), for both BYOH and CAPO.
  // Case B: infrastructure provisioning stuck (Provisioning phase, no providerID) ->
  //   InfrastructureProvisionTimeout, after a provider-specific threshold - BYOH_PROVISION_STUCK_MS
  //   (8 min) for BYOH, NODE_JOIN_TIMEOUT_MS (15 min) for CAPO.
  // Case C: BYOH only, past BYOH_PROVISION_SLOW_MS (4 min) but not yet stuck -> Slow, a soft
  //   heads-up that provisioning is taking longer than the observed healthy range.
  if (!status?.nodeRef) {
    const minutes = Math.round(age / 60000)
    const isByo = isByoMachine(machine)
    if ((phase === 'Provisioned' || !!machine?.spec?.providerID) && age > NODE_JOIN_TIMEOUT_MS) {
      return {
        state: MachineHealthState.Stuck,
        reason: 'NodeJoinTimeout',
        message: isByo
          ? `Bare metal host has not joined the cluster ${minutes} minutes after creation. Check /var/log/pf9/byoh/byoh-agent.log on the host for errors.`
          : `Node has not joined the cluster ${minutes} minutes after creation. The VM was created, so kubeadm bootstrap or the cluster join may have failed. Check the VM console log for bootstrap errors.`,
      }
    }
    if (phase === 'Provisioning') {
      const provisionStuckMs = isByo ? BYOH_PROVISION_STUCK_MS : NODE_JOIN_TIMEOUT_MS
      // The BYOH message names the supported host OS versions because an
      // unsupported OS is the most common cause here and the provider makes it
      // invisible: it logs "No k8s support for OS" but emits no Event, and
      // K8sInstallerConfig has no conditions field to carry a reason. The install
      // therefore never starts, which also leaves byoh-agent.log on the host
      // empty - so without the OS hint the log pointer alone is a dead end.
      //
      // The version list duplicates the provider's installer registry and will
      // drift with it. Replace it with a link once a supported-OS docs page
      // exists; the message is plain text rendered in a CodeBlock, so a link
      // needs a separate field on MachineHealth, not a URL inlined here.
      if (age > provisionStuckMs) {
        return {
          state: MachineHealthState.Stuck,
          reason: 'InfrastructureProvisionTimeout',
          message: isByo
            ? `Bare metal host provisioning has been in progress for ${minutes} minutes without completing. Check that the host OS is Ubuntu 22.04 or 24.04, and see /var/log/pf9/byoh/byoh-agent.log on the host for errors.`
            : `VM provisioning has been in progress for ${minutes} minutes without completing. The VM creation may be stuck due to offline compute hosts or infrastructure issues. Check hypervisor status.`,
        }
      }
      // Soft heads-up, BYOH only: CAPO has no equivalent because its own
      // instance-create timeout (10m, KAAP-1495) already sits inside the
      // 15-minute NodeJoinTimeout/InfrastructureProvisionTimeout window, so a
      // separate warning stage would fire on the same slow-but-healthy creates
      // that timeout was raised to tolerate.
      if (isByo && age > BYOH_PROVISION_SLOW_MS) {
        return {
          state: MachineHealthState.Slow,
          reason: INFRA_PROVISION_SLOW_REASON,
          message: `Bare metal host provisioning has been running for ${minutes} minutes without completing. It usually finishes sooner - if it does not complete shortly, confirm the host OS is Ubuntu 22.04 or 24.04, and check /var/log/pf9/byoh/byoh-agent.log on the host for errors.`,
        }
      }
    }
  }

  if (phase === 'Running') return { state: MachineHealthState.Ok }
  return { state: MachineHealthState.Progressing }
}

export const isUnhealthy = (h: MachineHealth): boolean =>
  !!h &&
  [MachineHealthState.Degraded, MachineHealthState.Stuck, MachineHealthState.Failed].includes(
    h.state,
  )

export const machineHealthSeverityRank: Record<MachineHealthState, number> = {
  [MachineHealthState.Failed]: 4,
  [MachineHealthState.Degraded]: 3,
  [MachineHealthState.Stuck]: 2,
  [MachineHealthState.Slow]: 1,
  [MachineHealthState.Ok]: 0,
  [MachineHealthState.Progressing]: 0,
}
